import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import useGet from "./hooks/useGet";
import axios from "axios";
import { Alert, Button, Loading } from 'fearless-common-ui';
import {concepts, Group} from "fearless-common";
import { OverlayTrigger, Tooltip, Modal } from "react-bootstrap";
import { PlusLg } from "react-bootstrap-icons";
import { backendEndpoints } from "../libs/backendEndpoints";
import { NO_LIMIT } from "./subscriptions/SeatsSpan";

interface InviteUser {
    id: number;
    first_name: string;
    is_subscribed?: boolean;
}

interface InviteCheckData {
    created_at: string;
}

interface ButtonState {
    label: ReactNode;
    variant: string;
    className?: string;
}

/** Returns the button label, variant, and class name based on the invite status data. */
function dataToText(data: InviteCheckData | false | undefined): ButtonState {
    if (!data) {
        return {
            label: <><PlusLg />{`add to ${concepts.Group.toLowerCase()}`}</>,
            variant: 'primary',
        };
    }
    if (Date.now() - new Date(data.created_at).getTime() < 60 * 5 * 1000) {
        return {
            label: 'Invited!',
            variant: 'outline-success',
            className: 'text-success',
        };
    }
    if (Date.now() - new Date(data.created_at).getTime() < 60 * 60 * 1000) {
        return {
            label: 'Recently invited',
            variant: 'outline-success',
            className: 'text-success',
        };
    }

    return {
        label: `Already in ${concepts.Group.toLowerCase()}`,
        variant: 'outline-light',
    };
}

function TextForNoSubscription({ hasRemainingSeats, user }: {
    hasRemainingSeats: boolean;
    user: InviteUser;
}) {
    return (
      <>
          {hasRemainingSeats ? (
            `${user.first_name} has no subscription and will take a seat on your subscription.`
          ) : (
            `${user.first_name} will be added to the ${concepts.Group.toLowerCase()}`
          )}
      </>
    );
}

/**
 * Button for inviting a user to a group. Shows real-time invite status, handles
 * subscription seat confirmation, and displays relevant error messages.
 */
export default function InviteButton({ user, groupId, onSuccess, groupIsSubscribed }: {
    /** The user to invite. */
    user: InviteUser;
    /** The ID of the group to invite the user to. */
    groupId: number;
    /** Called after the invite is successfully sent. */
    onSuccess: () => void;
    /** Whether the group has an active subscription requiring seat confirmation. */
    groupIsSubscribed?: boolean;
}) {
    const { data, isLoading, reload } = useGet<InviteCheckData | false>({
        endpoint: `/groups/${groupId}/check-user`, params: { user_id: user.id }
    });
    const { data: group } = useGet<Group>({
        endpoint: backendEndpoints.getGroup(groupId),
    });
    const [isInviting, setInviting] = useState(false);
    const [error, setErrorState] = useState<string | false>(false);
    const [show, setShow] = useState(false);
    const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const openModalBox = () => setShow(true);
    const closeModalBox = () => setShow(false);

    const setError = (message: string) => {
        setErrorState(message);
        clearTimeout(timeout.current);
        timeout.current = setTimeout(() => {
            setErrorState('');
        }, 4000);
    };

    const inviteUser = useCallback(() => {
        setErrorState('');
        setInviting(true);
        axios.post(backendEndpoints.inviteUserToGroup(groupId), { user: user.id })
            .then(() => {
                reload();
                closeModalBox();
                onSuccess();
            })
            .catch((e) => {
                setError(e.response?.data?.message);
            })
            .finally(() => setInviting(false));
    }, [groupId, user, reload, onSuccess]);

    const next = groupIsSubscribed ? openModalBox : inviteUser;
    const hasRemainingSeats = useMemo(() => {
        if (typeof group?.total_seats === 'number' && typeof group?.used_seats === 'number') {
            return group?.total_seats === NO_LIMIT || (group?.total_seats - group?.used_seats) > 0;
        }
        return false;
    }, [group?.total_seats, group?.used_seats]);

    const showModal = Boolean(show && user);

    useEffect(() => {
        if (showModal && !hasRemainingSeats) {
            inviteUser();
        }
    }, [hasRemainingSeats, inviteUser, showModal]);

    return (
        <>
            <OverlayTrigger
                overlay={
                    <Tooltip style={{ background: 'transparent' }}>
                        <Alert variant="danger" className="mb-0">{error}</Alert>
                    </Tooltip>
                }
                placement="left"
                show={!show && !!error}
            >
                <Button
                    size="sm"
                    disabled={isLoading}
                    variant={dataToText(data).variant}
                    className={dataToText(data).className}
                    onClick={isInviting || data ? undefined : next}
                >
                    {!isLoading && !isInviting && dataToText(data).label}
                    {(isLoading || isInviting) && <Loading />}
                </Button>
            </OverlayTrigger>
            {showModal && (
                <Modal show onHide={closeModalBox}>
                    <Modal.Header closeButton>
                        <Modal.Title className="text-center flex-grow-1 mb-0">Confirm seat</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="justify-content-center text-center py-5">
                        {user.is_subscribed
                            ? `${user.first_name} already has a subscription. We will give ${user.first_name} the option to choose how to arrange his/her subscription.`
                            : <TextForNoSubscription user={user} hasRemainingSeats={hasRemainingSeats} />}
                    </Modal.Body>
                    <Modal.Footer className="justify-content-center flex-column">
                        {error && <Alert className="alert-danger">{error}</Alert>}
                        <Button
                            size="lg"
                            disabled={isLoading}
                            variant="primary"
                            className="mb-0"
                            onClick={isInviting ? undefined : inviteUser}
                        >
                            {user?.is_subscribed ? `Add user` : `Add with Seat`}
                            {(isLoading || isInviting) && <Loading />}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
}
